import { create } from "zustand";
import type { Notification, NotificationType, ReferenceType } from "@/types/notification";
import { referenceTypeLabels } from "@/types/notification";

interface NotificationState {
  // State
  notifications: Notification[];
  unreadCount: number;
  isConnected: boolean;
  isDnd: boolean;

  // Actions
  setNotifications: (notifications: Notification[]) => void;
  addNotification: (notification: Notification) => void;
  markAsRead: (notificationId: number) => void;
  markAllAsRead: () => void;
  removeNotification: (notificationId: number) => void;
  setUnreadCount: (count: number) => void;
  incrementUnreadCount: () => void;
  setIsConnected: (connected: boolean) => void;
  setIsDnd: (isDnd: boolean) => void;
  clearNotifications: () => void;
}

export const useNotificationStore = create<NotificationState>((set) => ({
  notifications: [],
  unreadCount: 0,
  isConnected: false,
  isDnd: false,

  setNotifications: (notifications) =>
    set({ notifications }),

  addNotification: (notification) =>
    set((state) => {
      // 채팅 알림은 채팅방 구분 없이 최신 1개만 유지 (알림 중복 방지)
      if (notification.type === "chat" && notification.reference_type === "chat_room") {
        const existingIdx = state.notifications.findIndex(
          (n) => n.type === "chat" && n.reference_type === "chat_room" && !n.is_read
        );
        if (existingIdx !== -1) {
          // 기존 알림 제거 후 최신 알림을 맨 앞에 추가 (unreadCount 변동 없음)
          const filtered = state.notifications.filter((_, i) => i !== existingIdx);
          return { notifications: [notification, ...filtered] };
        }
      }
      return {
        notifications: [notification, ...state.notifications],
        unreadCount: notification.is_read ? state.unreadCount : state.unreadCount + 1,
      };
    }),

  markAsRead: (notificationId) =>
    set((state) => ({
      notifications: state.notifications.map((n) =>
        n.id === notificationId ? { ...n, is_read: true } : n
      ),
      unreadCount: Math.max(0, state.unreadCount - 1),
    })),

  markAllAsRead: () =>
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, is_read: true })),
      unreadCount: 0,
    })),

  removeNotification: (notificationId) =>
    set((state) => {
      const notification = state.notifications.find((n) => n.id === notificationId);
      const wasUnread = notification && !notification.is_read;
      return {
        notifications: state.notifications.filter((n) => n.id !== notificationId),
        unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
      };
    }),

  setUnreadCount: (count) =>
    set({ unreadCount: count }),

  incrementUnreadCount: () =>
    set((state) => ({ unreadCount: state.unreadCount + 1 })),

  setIsConnected: (connected) =>
    set({ isConnected: connected }),

  setIsDnd: (isDnd) =>
    set({ isDnd }),

  clearNotifications: () =>
    set({ notifications: [], unreadCount: 0 }),
}));

// 알림 타입별 라우트 매핑
// 참조 타입 라벨 조회
export const getReferenceTypeLabel = (referenceType?: string): string => {
  if (!referenceType) return "";
  return referenceTypeLabels[referenceType as ReferenceType] || referenceType;
};

export const getNotificationRoute = (
  _type: NotificationType,
  referenceType?: string,
  referenceId?: number,
  title?: string,
  url?: string
): string => {
  // DB에 저장된 URL이 있으면 우선 사용
  if (url) return url;

  // referenceId가 없어도 특정 페이지로 이동해야 하는 케이스 처리
  if (referenceType) {
    switch (referenceType) {
      // referenceId 필요한 케이스
      case "ticket":
        return referenceId ? `/maintenance/tickets/${referenceId}` : "/maintenance/tickets";
      case "task":
        return referenceId ? `/projects/tasks/${referenceId}` : "/projects";
      case "project":
        return referenceId ? `/projects/${referenceId}` : "/projects";
      case "issue":
        return referenceId ? `/issues/${referenceId}` : "/issues";
      case "chat_room":
        return referenceId ? `/chat/popup/${referenceId}` : "/chat";
      case "ea_document":
        return referenceId ? `/ea/documents/${referenceId}` : "/ea/inbox?box=to_approve";
      case "ea_delegation":
        return "/ea/inbox?box=to_approve";
      // 구 전자결재 참조 — 리다이렉트 라우트(EaLegacyRedirect)가 새 문서로 해석
      case "approval":
        return referenceId ? `/approval/${referenceId}` : "/ea/inbox?box=to_approve";
      case "purchase_request":
        return referenceId
          ? `/approval-management?type=purchase_request&id=${referenceId}`
          : "/ea/processing";
      case "card_approval":
        return referenceId
          ? `/approval-management?type=card_approval&id=${referenceId}`
          : "/ea/processing";
      case "expense":
        return referenceId
          ? `/approval-management?type=expense&id=${referenceId}`
          : "/ea/processing";
      case "business_trip":
        return referenceId ? `/business-trips/${referenceId}` : "/business-trips";
      case "customer_feedback":
        return referenceId ? `/customer-feedback/${referenceId}` : "/customer-feedback";
      case "employee_voice":
        return referenceId ? `/employee-voice/${referenceId}` : "/employee-voice";

      // referenceId 없이도 고정 경로로 이동하는 케이스
      case "leave":
        return "/attendance/leave";
      case "leave_approval":
        return "/ea/inbox?box=to_approve";
      case "approval_document":
        return referenceId ? `/approval/${referenceId}` : "/ea/inbox?box=to_approve";
      case "absence":
        return "/admin/attendance-unified";
      case "attendance":
        return "/attendance";
      case "schedule":
        return "/calendar";
      case "reservation":
        return "/reservation";
      case "lottery_ticket":
        return "/lottery";
      case "document_request":
        // 서류 제출 알림은 관리자 페이지로, 승인/반려 알림은 내 서류 페이지로
        if (title && title.includes("제출")) {
          return "/document-requests/admin";
        }
        return "/document-requests/my";
      case "post":
        return "/board/notice";
      case "corporate_card":
        return "/my-card";
      case "payslip":
        return "/payslip";

      default:
        return "/notifications";
    }
  }

  return "/notifications";
};
